/**
 * AnimatedTabs — branded tab bar with a sliding underline indicator.
 *
 * Why this exists:
 *   The original tab bars across user dashboard / provider workspace used a
 *   plain `border-b-2` per button, which produced a visible "jump" when
 *   switching tabs and overflowed awkwardly on narrow viewports. This
 *   component gives every tab bar a single shared underline that smoothly
 *   slides between active tabs, hides its scrollbar, and adds a subtle edge
 *   fade so users can tell when more tabs are off-screen.
 *
 * Design tokens:
 *   - Active tab text/underline use the Forest brand token by default; pass
 *     `accent="amber"` for the Amber accent.
 *   - All colors come from CSS variables in tokens.css — no hard-coded hex.
 */
import * as React from 'react';
import { cn } from '../lib/cn';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AnimatedTabItem<TValue extends string = string> {
  value:    TValue;
  label:    React.ReactNode;
  icon?:    React.ElementType;
  badge?:   React.ReactNode;
  disabled?: boolean;
  /** Tooltip / sr-only suffix shown when disabled. */
  disabledHint?: string;
}

export interface AnimatedTabsProps<TValue extends string = string> {
  items:     ReadonlyArray<AnimatedTabItem<TValue>>;
  value:     TValue;
  onChange:  (v: TValue) => void;
  /** Underline / active-text color. Defaults to forest. */
  accent?:   'forest' | 'amber';
  /** Visual density. */
  size?:     'sm' | 'md';
  /** ARIA label for the tab list. */
  ariaLabel?: string;
  className?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AnimatedTabs<TValue extends string = string>({
  items,
  value,
  onChange,
  accent    = 'forest',
  size      = 'md',
  ariaLabel = 'Tabs',
  className,
}: AnimatedTabsProps<TValue>) {
  const listRef     = React.useRef<HTMLDivElement | null>(null);
  const buttonsRef  = React.useRef<Map<TValue, HTMLButtonElement>>(new Map());
  const [indicator, setIndicator] = React.useState<{ left: number; width: number; ready: boolean }>({
    left: 0,
    width: 0,
    ready: false,
  });

  const recompute = React.useCallback(() => {
    const list = listRef.current;
    const btn  = buttonsRef.current.get(value);
    if (!list || !btn) {
      setIndicator((s) => ({ ...s, ready: false }));
      return;
    }
    const listRect = list.getBoundingClientRect();
    const btnRect  = btn.getBoundingClientRect();
    setIndicator({
      left:  btnRect.left - listRect.left + list.scrollLeft,
      width: btnRect.width,
      ready: true,
    });

    if (btn.offsetLeft < list.scrollLeft) {
      list.scrollTo({ left: btn.offsetLeft - 16, behavior: 'smooth' });
    } else if (btn.offsetLeft + btn.offsetWidth > list.scrollLeft + list.clientWidth) {
      list.scrollTo({
        left: btn.offsetLeft + btn.offsetWidth - list.clientWidth + 16,
        behavior: 'smooth',
      });
    }
  }, [value]);

  React.useLayoutEffect(() => {
    recompute();
  }, [recompute, items.length]);

  React.useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const ro = new ResizeObserver(recompute);
    ro.observe(list);
    window.addEventListener('resize', recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recompute);
    };
  }, [recompute]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const enabled = items.filter((it) => !it.disabled);
    if (enabled.length === 0) return;
    const currentIdx = enabled.findIndex((it) => it.value === value);
    let nextIdx = currentIdx;
    if      (e.key === 'ArrowLeft')  nextIdx = (currentIdx - 1 + enabled.length) % enabled.length;
    else if (e.key === 'ArrowRight') nextIdx = (currentIdx + 1) % enabled.length;
    else if (e.key === 'Home')       nextIdx = 0;
    else if (e.key === 'End')        nextIdx = enabled.length - 1;
    const next = enabled[nextIdx];
    if (next) onChange(next.value);
  }

  const padY  = size === 'sm' ? 'py-2.5' : 'py-3.5';
  const textY = size === 'sm' ? 'text-xs' : 'text-sm';

  const accentColor = accent === 'amber'
    ? 'hsl(var(--palette-amber))'
    : 'hsl(var(--palette-forest))';

  return (
    <div className={cn('border-b border-border', className)}>
      <div
        ref={listRef}
        role="tablist"
        aria-label={ariaLabel}
        onKeyDown={handleKeyDown}
        className="no-scrollbar fade-mask-x relative flex overflow-x-auto px-1"
      >
        {items.map((item) => {
          const Icon   = item.icon;
          const active = item.value === value;
          return (
            <button
              key={item.value}
              ref={(el) => {
                if (el) buttonsRef.current.set(item.value, el);
                else    buttonsRef.current.delete(item.value);
              }}
              type="button"
              role="tab"
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              disabled={item.disabled}
              onClick={() => !item.disabled && onChange(item.value)}
              className={cn(
                'relative inline-flex items-center gap-1.5 px-4 font-semibold whitespace-nowrap',
                'transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md',
                'disabled:opacity-40 disabled:cursor-not-allowed',
                padY,
                textY,
                active ? '' : 'text-muted-foreground hover:text-foreground',
              )}
              style={active ? { color: accentColor } : undefined}
            >
              {Icon && <Icon className={size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden />}
              {item.label}
              {item.badge !== undefined && item.badge !== null && (
                <span
                  className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full text-[10px] font-bold transition-colors"
                  style={
                    active
                      ? { backgroundColor: accentColor, color: 'hsl(var(--palette-cream))' }
                      : { backgroundColor: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }
                  }
                >
                  {item.badge}
                </span>
              )}
              {item.disabled && item.disabledHint && (
                <span className="text-[9px] opacity-60 ml-0.5">({item.disabledHint})</span>
              )}
            </button>
          );
        })}

        {/* Sliding underline */}
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-0 h-[2px] rounded-full transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{
            left:       indicator.left,
            width:      indicator.width,
            opacity:    indicator.ready ? 1 : 0,
            backgroundColor: accentColor,
          }}
        />
      </div>
    </div>
  );
}
